import { makeAutoObservable } from 'mobx'

import { BrandConfig } from '@/Ship/Configs/BrandConfig'

export type ThemeMode = 'light' | 'dark'

/**
 * Ключ у `localStorage`. Для Астерікса — історичний `wl-theme`; інший бренд
 * пам'ятає вибір окремо, інакше той, хто колись обрав світлу тему на
 * Астеріксі, ніколи не побачив би темний бренд таким, як його задумано (ADR 048).
 */
const STORAGE_KEY = BrandConfig.id === 'asterix' ? 'wl-theme' : `wl-theme:${BrandConfig.id}`

const readStoredMode = (): ThemeMode | null => {
    try {
        const value = window.localStorage.getItem(STORAGE_KEY)

        return value === 'light' || value === 'dark' ? value : null
    } catch {
        // Сховище недоступне (приватний режим, заборона cookie) — тема за замовчуванням
        return null
    }
}

/**
 * Позначка теми на `<html>`.
 *
 * За `data-theme` перемикаються кольорові змінні в CSS (`Ship/Components/Theme/theme.css`),
 * а `color-scheme` перефарбовує те, що малює сам браузер: смуги прокрутки,
 * нативні поля, тло сторінки до першого рендеру.
 */
const applyToDocument = (mode: ThemeMode): void => {
    document.documentElement.dataset['theme'] = mode
    document.documentElement.style.colorScheme = mode
}

/**
 * Світла чи темна тема інтерфейсу.
 *
 * ⚠️ Вибір живе в браузері, а не на акаунті, як режим підказок: тему треба
 * знати ДО входу — екран логіну вже має бути в ній — і до першого запиту,
 * інакше кожне відкриття сторінки починалося б зі спалаху світлої.
 *
 * За замовчуванням — тема бренду (`BrandProfile.defaultTheme`), для Астерікса
 * світла: це затверджений вигляд застосунку.
 */
class ThemeManager {
    mode: ThemeMode = readStoredMode() ?? BrandConfig.defaultTheme ?? 'light'

    constructor() {
        makeAutoObservable(this)
        applyToDocument(this.mode)
    }

    get isDark(): boolean {
        return this.mode === 'dark'
    }

    toggle(): void {
        this.setMode(this.isDark ? 'light' : 'dark')
    }

    setMode(mode: ThemeMode): void {
        this.mode = mode
        applyToDocument(mode)

        try {
            window.localStorage.setItem(STORAGE_KEY, mode)
        } catch {
            // Не запам'ятається між відкриттями — але перемкнеться
        }
    }
}

export const themeManager = new ThemeManager()
